import {
  Box,
  Button,
  Divider,
  MantineProvider,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import {useForm, zodResolver} from '@mantine/form';
import {useDisclosure} from '@mantine/hooks';
import {Notifications} from '@mantine/notifications';
import type {AppError} from '@packages/utils';
import Head from 'next/head';
import {useRouter} from 'next/router';
import type React from 'react';
import {COLOR_LINK_1} from '../../../config/constants/colorCodes';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {PostInquiryTarekomiSchema} from '../../../core/entities/inquiry/schema';
import {
  type PostInquiryTarekomiDataType,
  usePostInquiryTarekomi,
} from '../../../core/repositories/inquiryRepository';
import {useReCaptcha} from '../../../core/usecases/useReCaptcha';
import {CommonLink} from '../../../shared/components/CommonLink';
import {
  showErrorSnackBar,
  showSuccessSnackBar,
} from '../../../shared/components/SnackBar';
import {useIsAppWebView} from '../../../shared/hooks/useIsAppWebView';

type Props = Record<string, never>;

type CastedError = {message: AppError};

export const PostInquiryTarekomi: React.FC<Props> = () => {
  const isWebView = useIsAppWebView();
  const router = useRouter();
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.pathname)}`;
  const {reCaptcha} = useReCaptcha();

  const form = useForm({
    validate: zodResolver(PostInquiryTarekomiSchema),
    initialValues: {
      body: '',
      email: '',
      name: '',
    },
  });
  const saveInquiryTarekomi = usePostInquiryTarekomi({
    onSuccessFnc() {
      handlers.close();
      showSuccessSnackBar({title: '送信しました', message: 'done'});
    },
    onErrorFnc(error: unknown) {
      const castedError = error as CastedError;
      handlers.close();
      showErrorSnackBar({
        isNetworkError: castedError.message === 'NetworkError',
      });
    },
  });
  const [isLoading, handlers] = useDisclosure(false);

  const submitPost = (values: PostInquiryTarekomiDataType) => {
    if (isWebView) {
      saveInquiryTarekomi.mutate(
        values && {
          ...values,
        },
      );
    } else {
      reCaptcha('submit')
        .then(() => {
          handlers.open();
          saveInquiryTarekomi.mutate(
            values && {
              ...values,
            },
          );
        })
        .catch(() => {
          showErrorSnackBar({
            isNetworkError: false,
            title: 'reCAPTCHAエラー',
            message: 'reCAPTCHAエラー',
          });
        });
    }
  };

  return (
    <>
      <Head>
        <title key="title">就活情報のタレコミ</title>
        <meta key="og:title" property="og:title" content="就活情報のタレコミ" />
        <link rel="canonical" href={currentUrl} key="canonical" />
      </Head>

      <Box className="flex min-h-screen w-full max-w-[664px] flex-col items-center bg-background-1">
        <MantineProvider>
          <Notifications position={'top-right'} />
          <Box className="w-full max-w-[664px]">
            <Box
              sx={() => ({
                marginTop: '10px',
              })}
            >
              <Title
                order={1}
                className="border-t-4 border-primary-1 bg-white p-16 text-lg text-primary-1 pc:px-32"
              >
                就活情報のタレコミ
              </Title>
            </Box>

            <Box
              className=" bg-white p-16 pc:px-32"
              sx={() => ({
                marginTop: '10px',
                marginBottom: '20px',
              })}
            >
              <Box className="mb-24 py-16 ">
                <Text
                  className="mb-24"
                  sx={() => ({
                    fontSize: '14px',
                  })}
                >
                  外資就活ドットコムはタレコミを募集しています！
                </Text>
                <Text
                  sx={() => ({
                    fontSize: '14px',
                  })}
                >
                  あなたの知っている就活情報や就活ノウハウなど、なんでも教えてください。
                  <br />
                  なお、ここで頂いた情報は、当サイトの記事内で紹介することがあります。
                  <br />
                  （公表ＮＧな情報であれば、本文中にその旨をお書きください）
                </Text>

                <Text
                  sx={() => ({
                    fontSize: '14px',
                    marginTop: '2rem',
                    marginBottom: '2rem',
                  })}
                >
                  また、コラムとしてとりあげて欲しいテーマ、タイトルなどありましたらご連絡ください！
                  <br />
                  個別に詳細をお伺いしても良い場合は、連絡先もご記入ください。
                </Text>

                <Text
                  fz={'xs'}
                  sx={() => ({
                    marginBottom: '1rem',
                  })}
                >
                  （テーマ・タイトル例）
                </Text>
                <ul className="list-disc pl-20">
                  <li className="text-base leading">
                    『トップ企業の人事に聞く！「雑談だけ」最終面接の正体は？』
                  </li>
                  <li className="mt-20 text-base leading">
                    『極限までムダを減らしたい！シンプル節約就活の勝率』※ルポ風で
                  </li>
                  <li className="mt-20 text-base leading">
                    <span className="inline-block w-4 pc:w-8" />
                    業界別の各社エントリー時期、イベントカレンダーまとめ
                    などなど
                  </li>
                </ul>
              </Box>
              <form
                onSubmit={form.onSubmit((values) => {
                  submitPost(values);
                })}
              >
                <Textarea
                  placeholder="タレコミ内容"
                  label="タレコミ内容"
                  withAsterisk={true}
                  {...form.getInputProps('body')}
                  sx={() => ({
                    marginBottom: '1rem',
                  })}
                />

                <TextInput
                  label="お名前"
                  placeholder="お名前"
                  withAsterisk={false}
                  {...form.getInputProps('name')}
                  sx={() => ({
                    marginBottom: '1rem',
                  })}
                />
                <TextInput
                  label="メールアドレス"
                  placeholder="メールアドレス"
                  withAsterisk={false}
                  {...form.getInputProps('email')}
                  sx={() => ({
                    marginBottom: '1rem',
                  })}
                />

                <Divider my="lg" />
                <Box className="px-8 py-16 pc:px-32">
                  <Text
                    sx={() => ({
                      fontSize: '14px',
                    })}
                  >
                    個人情報の利用について、
                    <CommonLink href="/pmark" className="text-link">
                      「個人情報の取扱い」
                    </CommonLink>
                    を必ずご確認ください。
                  </Text>
                </Box>

                <Box className="px-8 py-16 pc:px-32">
                  <Button
                    fullWidth
                    variant="outline"
                    loading={isLoading}
                    type="submit"
                    styles={() => ({
                      label: {
                        color: COLOR_LINK_1,
                        fontWeight: 300,
                      },
                      root: {
                        borderColor: COLOR_LINK_1,
                      },
                    })}
                  >
                    「個人情報の取り扱い」に合意して送信
                  </Button>
                </Box>
              </form>
            </Box>
          </Box>
        </MantineProvider>
      </Box>
    </>
  );
};
